import { useEffect, useRef } from "react";
import { usePrefersReducedMotion } from "../hooks/usePrefersReducedMotion";

/**
 * Custom cursor w stylu Lusion: mała kropka śledzi kursor 1:1,
 * większy pierścień goni ją z opóźnieniem (lerp 0.14). Na elementach
 * interaktywnych (a, button, [data-cursor="hover"]) pierścień rośnie
 * i wypełnia się kolorem accent z mix-blend-difference.
 *
 * - Nie ukrywamy natywnego kursora globalnie (dostępność!) - kropka
 *   i pierścień są warstwą PONAD kursorem, pointer-events: none.
 *   Jeśli chcesz pełny efekt Lusion (bez natywnego kursora), odkomentuj
 *   linijkę z `document.body.style.cursor = "none"` - świadoma decyzja.
 * - Tylko pointer: fine. Na dotyku i przy reduced-motion nie renderuje się.
 *
 * Wpięcie: raz w App, obok GrainOverlay.
 */
export function CustomCursor({ accent = "#BEF264" }: { accent?: string }) {
  const dotRef = useRef<HTMLDivElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);
  const reducedMotion = usePrefersReducedMotion();

  const enabled =
    !reducedMotion &&
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(pointer: fine)").matches;

  useEffect(() => {
    if (!enabled) return;

    // document.body.style.cursor = "none"; // pełny efekt Lusion - opcjonalnie

    let mouseX = -100;
    let mouseY = -100;
    let ringX = -100;
    let ringY = -100;
    let hovering = false;
    let raf: number;

    function onMove(e: MouseEvent) {
      mouseX = e.clientX;
      mouseY = e.clientY;
      const target = e.target as HTMLElement | null;
      hovering = !!target?.closest?.('a, button, [role="button"], [data-cursor="hover"]');
    }

    function loop() {
      ringX += (mouseX - ringX) * 0.14;
      ringY += (mouseY - ringY) * 0.14;

      if (dotRef.current) {
        dotRef.current.style.transform = `translate3d(${mouseX}px, ${mouseY}px, 0) translate(-50%, -50%)`;
      }
      if (ringRef.current) {
        const scale = hovering ? 2.2 : 1;
        ringRef.current.style.transform = `translate3d(${ringX}px, ${ringY}px, 0) translate(-50%, -50%) scale(${scale})`;
        ringRef.current.style.background = hovering ? "rgba(190, 242, 100, 0.85)" : "transparent";
      }
      raf = requestAnimationFrame(loop);
    }

    window.addEventListener("mousemove", onMove, { passive: true });
    raf = requestAnimationFrame(loop);
    return () => {
      window.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(raf);
      // document.body.style.cursor = "";
    };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <>
      <div
        ref={dotRef}
        aria-hidden="true"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: accent,
          zIndex: 10001,
          pointerEvents: "none",
          willChange: "transform",
        }}
      />
      <div
        ref={ringRef}
        aria-hidden="true"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: 34,
          height: 34,
          borderRadius: "50%",
          border: `1px solid ${accent}`,
          mixBlendMode: "difference",
          zIndex: 10001,
          pointerEvents: "none",
          willChange: "transform",
          transition: "background 250ms ease",
        }}
      />
    </>
  );
}
